import type { FC } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField } from '@mui/material';
import { useFormikContext } from 'formik';

import type { FormField } from 'src/types/survey';

import type { FormFieldKeys } from '../constants';
import FollowUpInput from '../FollowUpInput';

interface SurveyTextInputProps {
  disabled?: boolean;
  field: FormField;
  index: number;
}

function hasValueProperty(error: any): error is { value: string } {
  return error && typeof error === 'object' && 'value' in error;
}

const SurveyTextInput: FC<SurveyTextInputProps> = ({
  field,
  disabled = false,
  index,
}) => {
  const { t } = useTranslation('followUp');
  const { handleChange, values, errors, touched } =
    useFormikContext<FormFieldKeys>();
  const errorMessage = useMemo(() => {
    if (!errors.customFields?.[index] || !touched.customFields?.[index])
      return undefined;
    if (typeof errors.customFields?.[index] === 'string') return undefined;
    if (hasValueProperty(errors.customFields?.[index]))
      return t('validationError.genericRequired');
  }, [errors.customFields?.[index], touched.customFields?.[index]]);

  return (
    <>
      <input
        hidden
        name={`customFields.${index}.customField`}
        value={field.id}
        readOnly
      />
      <input hidden name={`customFields.${index}.required`} readOnly />
      <FollowUpInput
        label={field.name + (field.isMandatory ? ' *' : '')}
        disabled={disabled}
        name={String(field.id)}
        helperText={field.description}
        sx={{ display: values.callHappened ? 'flex' : 'none', gap: 1 }}
        errorMessage={errorMessage}
      >
        <TextField
          id={String(field.id)}
          name={`customFields.${index}.value`}
          multiline
          rows={4}
          value={values.customFields[index]?.value}
          onChange={handleChange}
          disabled={disabled}
        />
      </FollowUpInput>
    </>
  );
};

export default SurveyTextInput;
